/**
 * Merchant menu, hours, and inventory writes.
 *
 * Unlike orders, these go straight to the tables through PostgREST rather than
 * through RPCs. Row Level Security already scopes every row to a restaurant the
 * caller staffs (`private.is_staff`), and there is no money to compute, so a
 * wrapper function per write would add surface without adding safety.
 */

import type {
  MerchantCategory,
  MerchantDiscount,
  MerchantMenuItem,
  MerchantRestaurant,
  OpeningException,
  OpeningHour,
} from '@yummy/shared';

import { unwrap, type DbClient } from './client';

/** Fields the menu editor may set on a category. */
export interface CategoryInput {
  name: string;
  description?: string | null;
  sort_order?: number;
  is_active?: boolean;
}

/**
 * Creates a menu category.
 *
 * @param client A Supabase client.
 * @param restaurantId Owning restaurant.
 * @param input Category fields.
 * @returns The created category.
 */
export async function createCategory(
  client: DbClient,
  restaurantId: string,
  input: CategoryInput,
): Promise<MerchantCategory> {
  return unwrap(
    await client
      .from('menu_categories')
      .insert({
        restaurant_id: restaurantId,
        name: input.name,
        description: input.description ?? null,
        sort_order: input.sort_order ?? 0,
        is_active: input.is_active ?? true,
      })
      .select()
      .single(),
  ) as unknown as MerchantCategory;
}

/**
 * Moves several menu items to one category, or to none.
 *
 * One call rather than one per item: moving six dishes with six requests can
 * half-succeed, and half a move is worse than none.
 *
 * @param client A Supabase client.
 * @param itemIds The items to move.
 * @param categoryId Where to put them, or null for Uncategorised.
 * @returns How many were moved.
 */
export async function moveMenuItems(
  client: DbClient,
  itemIds: string[],
  categoryId: string | null,
): Promise<number> {
  return unwrap(
    await client.rpc('move_menu_items', {
      p_ids: itemIds,
      p_category_id: categoryId ?? undefined,
    }),
  ) as unknown as number;
}

/**
 * Updates a menu category.
 *
 * @param client A Supabase client.
 * @param categoryId Category to update.
 * @param patch Fields to change.
 * @returns The updated category.
 */
export async function updateCategory(
  client: DbClient,
  categoryId: string,
  patch: Partial<CategoryInput>,
): Promise<MerchantCategory> {
  return unwrap(
    await client.from('menu_categories').update(patch).eq('id', categoryId).select().single(),
  ) as unknown as MerchantCategory;
}

/**
 * Deletes a menu category.
 *
 * Items in it are not deleted; `menu_items.category_id` is `on delete set null`,
 * so they move to the menu's uncategorised section.
 *
 * @param client A Supabase client.
 * @param categoryId Category to delete.
 */
export async function deleteCategory(client: DbClient, categoryId: string): Promise<void> {
  const { error } = await client.from('menu_categories').delete().eq('id', categoryId);
  if (error) throw error;
}

/** Fields the menu editor may set on an item. */
export interface MenuItemInput {
  category_id?: string | null;
  name: string;
  description?: string | null;
  price_cents: number;
  photo_url?: string | null;
  photo_path?: string | null;
  sort_order?: number;
  is_active?: boolean;
  track_inventory?: boolean;
  inventory_count?: number | null;
  prep_minutes_baseline?: number;
  min_quantity?: number;
}

/**
 * Creates a menu item.
 *
 * @param client A Supabase client.
 * @param restaurantId Owning restaurant.
 * @param input Item fields.
 * @returns The created item.
 */
export async function createMenuItem(
  client: DbClient,
  restaurantId: string,
  input: MenuItemInput,
): Promise<MerchantMenuItem> {
  return unwrap(
    await client
      .from('menu_items')
      .insert({
        restaurant_id: restaurantId,
        category_id: input.category_id ?? null,
        name: input.name,
        description: input.description ?? null,
        price_cents: input.price_cents,
        photo_url: input.photo_url ?? null,
        photo_path: input.photo_path ?? null,
        sort_order: input.sort_order ?? 0,
        is_active: input.is_active ?? true,
        track_inventory: input.track_inventory ?? false,
        inventory_count: input.inventory_count ?? null,
        prep_minutes_baseline: input.prep_minutes_baseline ?? 0,
        // Named here as well as on the type: this list is what actually reaches
        // the insert, so a field the type allows but this omits silently takes
        // the column's default. That is how a new dish ignored its minimum.
        min_quantity: input.min_quantity ?? 1,
      })
      .select()
      .single(),
  ) as unknown as MerchantMenuItem;
}

/**
 * Updates a menu item.
 *
 * @param client A Supabase client.
 * @param itemId Item to update.
 * @param patch Fields to change.
 * @returns The updated item.
 */
export async function updateMenuItem(
  client: DbClient,
  itemId: string,
  patch: Partial<MenuItemInput>,
): Promise<MerchantMenuItem> {
  return unwrap(
    await client.from('menu_items').update(patch).eq('id', itemId).select().single(),
  ) as unknown as MerchantMenuItem;
}

/**
 * Deletes a menu item.
 *
 * Past orders keep their own name and price snapshot, so deleting an item never
 * rewrites order history.
 *
 * @param client A Supabase client.
 * @param itemId Item to delete.
 */
export async function deleteMenuItem(client: DbClient, itemId: string): Promise<void> {
  const { error } = await client.from('menu_items').delete().eq('id', itemId);
  if (error) throw error;
}

/**
 * Deletes several menu items in one request.
 *
 * A loop of single deletes can half-succeed, and half a deleted selection is
 * worse than none — the merchant cannot tell what is left without counting.
 *
 * @param client A Supabase client.
 * @param itemIds The items to remove.
 * @returns Nothing.
 */
export async function deleteMenuItems(client: DbClient, itemIds: string[]): Promise<void> {
  if (itemIds.length === 0) return;
  const { error } = await client.from('menu_items').delete().in('id', itemIds);
  if (error) throw error;
}

/**
 * Sets the exact inventory count for an item.
 *
 * @param client A Supabase client.
 * @param itemId Item to update.
 * @param count New count; ignored by the server when `track_inventory` is off.
 * @returns The updated item.
 */
export async function setInventory(
  client: DbClient,
  itemId: string,
  count: number,
): Promise<MerchantMenuItem> {
  return updateMenuItem(client, itemId, { inventory_count: Math.max(0, Math.trunc(count)) });
}

// ---------------------------------------------------------------------------
// Availability windows
// ---------------------------------------------------------------------------

export interface AvailabilityWindowInput {
  weekday: number;
  start_time: string;
  end_time: string;
}

/**
 * Replaces an item's availability windows.
 *
 * Delete-then-insert rather than a diff: the editor presents the windows as one
 * set, so an exact replacement keeps the client simple and avoids duplicate-key
 * conflicts on the `(item_id, weekday, start, end)` unique constraint.
 *
 * An empty list means "available whenever the restaurant is open".
 *
 * @param client A Supabase client.
 * @param itemId Item to update.
 * @param windows The complete new set.
 */
export async function setAvailabilityWindows(
  client: DbClient,
  itemId: string,
  windows: AvailabilityWindowInput[],
): Promise<void> {
  const { error: deleteError } = await client
    .from('item_availability_windows')
    .delete()
    .eq('item_id', itemId);
  if (deleteError) throw deleteError;

  if (windows.length === 0) return;

  const { error } = await client.from('item_availability_windows').insert(
    windows.map((w) => ({
      item_id: itemId,
      weekday: w.weekday,
      start_time: w.start_time,
      end_time: w.end_time,
    })),
  );
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Discounts
// ---------------------------------------------------------------------------

export interface DiscountInput {
  label?: string | null;
  kind: 'percent' | 'amount';
  value: number;
  starts_at: string;
  ends_at: string;
  is_active?: boolean;
}

/**
 * Creates a time-boxed discount on an item.
 *
 * Overlapping discounts are allowed; the customer always receives the single
 * best one, resolved by `private.best_discount_cents`.
 *
 * @param client A Supabase client.
 * @param restaurantId Owning restaurant.
 * @param itemId Item to discount.
 * @param input Discount fields.
 * @returns The created discount.
 */
export async function createDiscount(
  client: DbClient,
  restaurantId: string,
  itemId: string,
  input: DiscountInput,
): Promise<MerchantDiscount> {
  return unwrap(
    await client
      .from('item_discounts')
      .insert({
        restaurant_id: restaurantId,
        item_id: itemId,
        label: input.label ?? null,
        kind: input.kind,
        value: input.value,
        starts_at: input.starts_at,
        ends_at: input.ends_at,
        is_active: input.is_active ?? true,
      })
      .select()
      .single(),
  ) as unknown as MerchantDiscount;
}

/**
 * Updates a discount.
 *
 * @param client A Supabase client.
 * @param discountId Discount to update.
 * @param patch Fields to change.
 * @returns The updated discount.
 */
export async function updateDiscount(
  client: DbClient,
  discountId: string,
  patch: Partial<DiscountInput>,
): Promise<MerchantDiscount> {
  return unwrap(
    await client.from('item_discounts').update(patch).eq('id', discountId).select().single(),
  ) as unknown as MerchantDiscount;
}

/**
 * Deletes a discount, immediately restoring the base price.
 *
 * @param client A Supabase client.
 * @param discountId Discount to delete.
 */
export async function deleteDiscount(client: DbClient, discountId: string): Promise<void> {
  const { error } = await client.from('item_discounts').delete().eq('id', discountId);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Opening hours
// ---------------------------------------------------------------------------

/**
 * Replaces the whole weekly schedule.
 *
 * Through `save_opening_hours` rather than an upsert. A weekday holds several
 * windows now, so there is no longer one key to conflict on, and replacing the
 * week has to be a delete and an insert in one transaction. The RPC is also
 * where the database refuses two windows that overlap, which is the only place
 * that check cannot be bypassed.
 *
 * @param client A Supabase client.
 * @param restaurantId Restaurant to update.
 * @param hours The week's rows, one per window.
 */
export async function saveOpeningHours(
  client: DbClient,
  restaurantId: string,
  hours: Array<Pick<OpeningHour, 'weekday' | 'is_closed' | 'open_time' | 'close_time'>>,
): Promise<void> {
  const { error } = await client.rpc('save_opening_hours', {
    p_restaurant_id: restaurantId,
    p_hours: hours.map((h) => ({
      weekday: h.weekday,
      is_closed: h.is_closed,
      open_time: h.open_time ?? '09:00',
      close_time: h.close_time ?? '21:00',
    })),
  });
  if (error) throw error;
}

/**
 * Adds or replaces a date-specific hours exception.
 *
 * @param client A Supabase client.
 * @param restaurantId Restaurant to update.
 * @param exception The date and its hours.
 */
export async function saveHoursException(
  client: DbClient,
  restaurantId: string,
  exception: Omit<OpeningException, 'id'>,
): Promise<void> {
  const { error } = await client.from('opening_hour_overrides').upsert(
    {
      restaurant_id: restaurantId,
      on_date: exception.on_date,
      is_closed: exception.is_closed,
      open_time: exception.open_time,
      close_time: exception.close_time,
      note: exception.note ?? null,
    },
    { onConflict: 'restaurant_id,on_date' },
  );
  if (error) throw error;
}

/**
 * Removes a date-specific exception, restoring the weekly rule for that date.
 *
 * @param client A Supabase client.
 * @param restaurantId Restaurant to update.
 * @param onDate The date to release.
 */
export async function deleteHoursException(
  client: DbClient,
  restaurantId: string,
  onDate: string,
): Promise<void> {
  const { error } = await client
    .from('opening_hour_overrides')
    .delete()
    .eq('restaurant_id', restaurantId)
    .eq('on_date', onDate);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Restaurant settings
// ---------------------------------------------------------------------------

/** Operational settings the merchant may change after onboarding. */
export interface RestaurantSettingsInput {
  name?: string;
  description?: string | null;
  phone?: string | null;
  /** Replaceable, never removable: the column is `not null`. */
  address?: string;
  timezone?: string;
  /**
   * Still accepted here, though the settings screen no longer offers it. The
   * lock is that screen's rule and nothing below it refuses the change, so a
   * caller that is not that screen can still set this. See
   * `docs/known-issues.md`.
   */
  currency?: string;
  accepting_orders?: boolean;
  /** Have non-scheduled orders start in the kitchen rather than awaiting acceptance. */
  auto_accept_orders?: boolean;
  tax_rate_bps?: number;
  min_lead_minutes?: number;
  max_advance_days?: number;
  slot_interval_minutes?: number;
  default_prep_minutes?: number;
  min_prep_minutes?: number;
  max_prep_minutes?: number;
  kitchen_capacity?: number;
  accept_grace_seconds?: number;
  accept_grace_basis?: 'placed' | 'estimate';
  /** Seconds a ready order may sit on the counter before the board chases it. */
  ready_chase_seconds?: number;
  /** Seconds one cooking round covers on the To cook page. A minute at least. */
  cook_round_seconds?: number;
  logo_url?: string | null;
  cover_url?: string | null;
  is_active?: boolean;
}

/**
 * Updates restaurant settings.
 *
 * @param client A Supabase client.
 * @param restaurantId Restaurant to update.
 * @param patch Fields to change.
 * @returns The updated settings.
 */
export async function updateRestaurant(
  client: DbClient,
  restaurantId: string,
  patch: RestaurantSettingsInput,
): Promise<MerchantRestaurant> {
  return unwrap(
    await client.from('restaurants').update(patch).eq('id', restaurantId).select().single(),
  ) as unknown as MerchantRestaurant;
}

/**
 * Replaces a dish's options with the given list.
 *
 * Wholesale rather than incremental: the editor shows the whole list, so what
 * it sends is what the dish should have. Options the merchant removed are
 * deleted, and their ids are preserved for the ones that stayed so past orders
 * keep pointing at them.
 *
 * @param client A Supabase client.
 * @param itemId The dish the options belong to.
 * @param restaurantId Owning restaurant.
 * @param variants The options, in display order.
 * @returns Nothing.
 */
export interface OptionGroupInput {
  name: string;
  /** 'absolute' means the choice is the dish's price; 'delta' means it adds. */
  price_mode: 'absolute' | 'delta';
  min_select: number;
  max_select: number;
  choices: Array<{ name: string; price_cents: number; is_active?: boolean }>;
}

/**
 * Replaces a dish's option groups with the set given.
 *
 * Wholesale rather than a diff, because the editor holds the whole set: a group
 * missing from it has been removed, and one with no choices is a half-typed row
 * rather than an instruction. Choices cascade with their group, so nothing is
 * orphaned.
 *
 * @param client A client able to write as this restaurant's staff.
 * @param itemId The dish whose groups these are.
 * @param restaurantId The restaurant, carried on both rows for the policies.
 * @param groups Every group the dish should end up with, in display order.
 * @returns Nothing.
 * @throws If a group cannot be created.
 */
export async function setItemOptionGroups(
  client: DbClient,
  itemId: string,
  restaurantId: string,
  groups: OptionGroupInput[],
): Promise<void> {
  await client.from('option_groups').delete().eq('item_id', itemId);

  for (const [index, group] of groups.entries()) {
    const created = unwrap(
      await client
        .from('option_groups')
        .insert({
          item_id: itemId,
          restaurant_id: restaurantId,
          name: group.name,
          price_mode: group.price_mode,
          min_select: group.min_select,
          max_select: group.max_select,
          sort_order: index,
        })
        .select()
        .single(),
    ) as { id: string } | null;

    if (!created) continue;

    const choices = group.choices.filter((choice) => choice.name.trim() !== '');
    if (choices.length === 0) continue;

    // Every column on every row: a bulk insert sends the union of the keys, so a
    // row omitting one gets NULL rather than the column's default.
    unwrap(
      await client.from('option_choices').insert(
        choices.map((choice, choiceIndex) => ({
          group_id: created.id,
          restaurant_id: restaurantId,
          name: choice.name,
          price_cents: choice.price_cents,
          sort_order: choiceIndex,
          is_active: choice.is_active ?? true,
        })),
      ),
    );
  }
}

export async function setItemVariants(
  client: DbClient,
  itemId: string,
  restaurantId: string,
  variants: Array<{ id?: string; name: string; price_cents: number; is_active?: boolean }>,
): Promise<void> {
  const { data: existing } = await client
    .from('menu_item_variants')
    .select('id')
    .eq('item_id', itemId);
  const keep = new Set(variants.map((v) => v.id).filter(Boolean) as string[]);
  const drop = (existing ?? []).map((r) => r.id).filter((id) => !keep.has(id));
  if (drop.length > 0) {
    await client.from('menu_item_variants').delete().in('id', drop);
  }
  if (variants.length === 0) return;

  // Every column is named on every row: a bulk insert sends the union of the
  // keys, so a row that omits one gets NULL rather than the column default.
  await client.from('menu_item_variants').upsert(
    variants.map((v, index) => ({
      ...(v.id ? { id: v.id } : {}),
      item_id: itemId,
      restaurant_id: restaurantId,
      name: v.name,
      price_cents: v.price_cents,
      sort_order: index,
      is_active: v.is_active ?? true,
    })),
  );
}

// ---------------------------------------------------------------------------
// Tables
// ---------------------------------------------------------------------------

/**
 * Creates a printable QR location.
 *
 * @param client A Supabase client.
 * @param restaurantId Owning restaurant.
 * @param label Printed name, for example `Counter` or `Table 4`.
 * @param seats Optional seat count.
 * @returns The new table's id and label.
 */
export async function createTable(
  client: DbClient,
  restaurantId: string,
  label: string,
  seats?: number | null,
): Promise<{ id: string; label: string }> {
  return unwrap(
    await client
      .from('restaurant_tables')
      .insert({ restaurant_id: restaurantId, label, seats: seats ?? null })
      .select('id, label')
      .single(),
  ) as unknown as { id: string; label: string };
}

/**
 * Enables or disables a table sticker without deleting its history.
 *
 * @param client A Supabase client.
 * @param tableId Table to update.
 * @param isActive New state.
 */
export async function setTableActive(
  client: DbClient,
  tableId: string,
  isActive: boolean,
): Promise<void> {
  const { error } = await client
    .from('restaurant_tables')
    .update({ is_active: isActive })
    .eq('id', tableId);
  if (error) throw error;
}

/**
 * Deletes a table sticker permanently; its token stops resolving.
 *
 * @param client A Supabase client.
 * @param tableId Table to delete.
 */
export async function deleteTable(client: DbClient, tableId: string): Promise<void> {
  const { error } = await client.from('restaurant_tables').delete().eq('id', tableId);
  if (error) throw error;
}
